import { Client, IMessage } from '@stomp/stompjs';
import { LoginFormData } from './Login.types';
import { useEffect, useRef, useState } from 'react';
import { LoginRestrictedError } from '@pages/LoginRestricted/LoginRestricted';
import { useNavigate } from 'react-router-dom';
import {
  AccessAppRecordStatusEnum,
  UserBasicTokenResponse,
  WebsocketSignInResponseStatusEnum,
} from '@features/api/response/User';
import {
  useLazyCheckLoginStatusQuery,
  useLazyUserQuery,
  useSignInMutation,
  useSignInWithSessionIdMutation,
} from '@features/api/userSlice';
import {
  encryptedStorage,
  EncryptedStorageKeys,
} from '@setup/encryptedStorage/encryptedStorage';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import SockJS from 'sockjs-client';
import { ApiBaseUrl } from '@utils/environmentVariables/environmentVariables';
import { Paths } from '@features/api/paths/paths';
import { WebsocketResponse } from '@features/api/response/WebsocketResponse';
import { delay } from 'lodash';

export const useWebsocket = (
  oldSessionId: string | null,
  setOldSessionId: (sessionId: string) => void,
  isLoading: boolean,
  setIsLoading: (isLoading: boolean) => void,
  handleContinue: (isEmailVerified?: boolean) => void,
  handleLogin: (data: LoginFormData) => Promise<NodeJS.Timeout | undefined>
) => {
  const intl = useIntl();

  const client = useRef<Client | null>(null);

  const [triggerGetUser] = useLazyUserQuery();

  useEffect(() => {
    return () => {
      deactivateWebsocket(client);
    };
  }, []);

  const handleMessage = async (data: LoginFormData, message: IMessage) => {
    console.log('Message received', message.body);
    const response: WebsocketResponse<
      UserBasicTokenResponse,
      WebsocketSignInResponseStatusEnum
    > = JSON.parse(message.body ?? '{}');
    if (!client.current) return;

    setIsLoading(true);

    if (response.status === WebsocketSignInResponseStatusEnum.USER_CREATED) {
      await deactivateWebsocket(client);

      delay(async () => {
        console.log('User created calling handlelogin');
        await handleLogin(data);
        setIsLoading(false);
      }, 1000);
    } else if (
      response.status === WebsocketSignInResponseStatusEnum.ERROR.toString()
    ) {
      console.error('Login error', message.body);
      toast.error(intl.formatMessage({ id: 'login.error' }));
    } else {
      encryptedStorage.setItem(
        EncryptedStorageKeys.AccessToken,
        response.data.accessToken
      );

      const { data: userData } = await triggerGetUser();
      handleContinue(userData?.emailVerified);
    }

    setIsLoading(false);
  };

  const deactivateWebsocket = async (
    client: React.MutableRefObject<Client | null>
  ) => {
    if (client.current) {
      await client.current.deactivate();
      console.log('Websocket deactivated');
    }
  };

  const activateWebsocket = async (data: LoginFormData, sessionId: string) => {
    console.log('Activating websocket');
    if (!sessionId || oldSessionId === sessionId) return;
    setOldSessionId(sessionId);

    await deactivateWebsocket(client);

    const socket = new SockJS(`${ApiBaseUrl}${Paths.WebSocket}`, [], {
      sessionId: () => sessionId,
    });
    client.current = new Client({
      webSocketFactory: () => socket,
      reconnectDelay: 5000,
      onConnect: () => {
        console.log('Websocket connected');
        if (client.current) {
          client.current.subscribe('/user/queue/updates', (message) =>
            handleMessage(data, message)
          );
        }
      },
      onStompError: (frame) => {
        console.error('Broker reported error:', frame.headers['message']);
        console.error('Additional details:', frame.body);
        toast.error(intl.formatMessage({ id: 'login.error' }));
      },
      onWebSocketError: (event) => {
        console.error('Websocket error:', event);
      },
    });

    client.current.activate();
  };

  return { activateWebsocket };
};

export const useLogin = (
  isMobile: boolean,
  handleContinue: (isEmailVerified?: boolean) => void
) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const [triggerGetUser] = useLazyUserQuery();
  const [loginUserMutation] = useSignInMutation();
  const [loginWithSessionId] = useSignInWithSessionIdMutation();
  const [triggerGetLoginStatus] = useLazyCheckLoginStatusQuery();

  const infoPanelOpen = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<AccessAppRecordStatusEnum | null>(null);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const setInfoPanelOpen = (isOpen: boolean) => {
    infoPanelOpen.current = isOpen;
  };

  useEffect(() => {
    return () => setInfoPanelOpen(false);
  }, []);

  const handleError = (e: any) => {
    const { status, data } = e;
    if (status === 403) {
      if (
        data.detail === LoginRestrictedError.Terminated ||
        data.detail === LoginRestrictedError.Suspended
      )
        navigate('/suspended');
    }
    console.error(e);
    setIsLoading(false);
  };

  const signInWithSessionId = async (
    formData: LoginFormData,
    sessionId: string
  ) => {
    if (!formData || !sessionId) return;

    try {
      setIsLoading(true);
      const { accessToken } = await loginWithSessionId({
        email: formData.emailAddress ?? '',
        password: formData.password ?? '',
        sessionId: sessionId ?? '',
      }).unwrap();

      encryptedStorage.setItem(EncryptedStorageKeys.AccessToken, accessToken);

      const { data: userData } = await triggerGetUser();

      setIsLoading(false);

      handleContinue(userData?.emailVerified);
    } catch (e: any) {
      handleError(e);
    }
  };

  const loginUser = async (data: LoginFormData, isMobile: boolean) => {
    if (!data.emailAddress || !data.password) {
      return Promise.reject('Invalid data');
    }

    return await loginUserMutation({
      email: data.emailAddress,
      password: data.password,
      keepUserLoggedIn: data.keepUserLoggedIn,
      isMobile: isMobile,
    }).unwrap();
  };

  const handleLoginClick = async (
    data: LoginFormData,
    tempSessionId?: string
  ): Promise<NodeJS.Timeout | undefined> => {
    if (!infoPanelOpen.current) return;
    if (!data.emailAddress || !data.password) {
      setIsLoading(false);
      return;
    }

    try {
      const loginStatus = await triggerGetLoginStatus({
        email: data.emailAddress,
        password: data.password,
        keepUserLoggedIn: data.keepUserLoggedIn,
        isMobile: isMobile,
      }).unwrap();

      setStatus(loginStatus);

      if (
        !isMobile &&
        loginStatus !== AccessAppRecordStatusEnum.ACCOUNT_DISABLED
      ) {
        const responseData = await loginUser(data, isMobile);
        if (responseData.qr) {
          setQrImage(`data:image/png;base64,${responseData.qr}`);
        }

        activateWebsocket(data, responseData.sessionId);
        return;
      }

      if (loginStatus === AccessAppRecordStatusEnum.LOGIN_ERROR) {
        return setTimeout(() => handleLoginClick(data, tempSessionId), 5000);
      }

      if (
        loginStatus === AccessAppRecordStatusEnum.LOGIN_SUCCESSFUL &&
        tempSessionId
      ) {
        await signInWithSessionId(data, tempSessionId);
        return;
      } else if (
        loginStatus === AccessAppRecordStatusEnum.RECORD_NOT_FOUND ||
        loginStatus === AccessAppRecordStatusEnum.READY
      ) {
        const responseData = await loginUser(data, isMobile);
        setSessionId(responseData.sessionId);

        return setTimeout(
          () => handleLoginClick(data, responseData.sessionId),
          5000
        );
      } else if (
        loginStatus === AccessAppRecordStatusEnum.LOGIN_PENDING ||
        loginStatus === AccessAppRecordStatusEnum.INITIALIZED
      ) {
        return setTimeout(() => handleLoginClick(data, tempSessionId), 3000);
      }
    } catch (e: any) {
      if (
        e.status === 403 &&
        (e.data?.detail === 'USER NOT FOUND' ||
          e.data?.detail === 'WRONG PASSWORD')
      ) {
        return;
      }

      handleError(e);

      const loginStatus = await triggerGetLoginStatus({
        email: data.emailAddress,
        password: data.password,
        keepUserLoggedIn: data.keepUserLoggedIn,
        isMobile: isMobile,
      }).unwrap();

      // Means that the call failed due to the website being in the background
      if (
        loginStatus === AccessAppRecordStatusEnum.LOGIN_SUCCESSFUL &&
        tempSessionId &&
        e.status === 'FETCH_ERROR' &&
        e.error?.includes('Load failed')
      ) {
        await signInWithSessionId(data, tempSessionId);
      }
    }
  };

  const { activateWebsocket } = useWebsocket(
    sessionId,
    setSessionId,
    isLoading,
    setIsLoading,
    handleContinue,
    handleLoginClick
  );

  const onBackClick = () => {
    setInfoPanelOpen(false);
    setQrImage(null);
    setStatus(null);
  };

  return {
    handleLoginClick,
    onBackClick,
    setInfoPanelOpen,
    resendNotification: loginUser,
    isLoading,
    qrImage,
    status,
  };
};
